/**
 * Carousel vidéo vertical SharpMod — texte à gauche, pistes scroll-snap, flou sur les côtés.
 *
 * Chargement : aucune vidéo ne se télécharge avec la page (preload="none", sources en data-src).
 * Les posters (data-poster) sont posés quand le carrousel approche de l'écran ; les sources d'une
 * vidéo, seulement quand elle est la vidéo centrale et que le carrousel est visible.
 * Lecture : une seule vidéo à la fois, la centrale. Pause hors écran et quand l'onglet est masqué.
 * Avec prefers-reduced-motion, rien ne démarre seul : le bouton lecture/pause lance la vidéo.
 */
class SmVideoShowcase extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.sm-vs__track');
    if (!this.track) return;

    this.slides = () => Array.from(this.track.querySelectorAll('.sm-vs__slide'));
    this.btn = this.querySelector('.sm-vs__cta');
    this.btnText = this.querySelector('.sm-vs__cta-text');
    this.btnPrev = this.querySelector('.sm-vs__nav--prev');
    this.btnNext = this.querySelector('.sm-vs__nav--next');
    this.openNewTab = this.dataset.openNewTab === 'true';

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.currentActiveSlide = null;
    this._scrollTicking = false;
    this.inView = false;
    // Lecture voulue : coupée par le bouton pause, et d'office si le visiteur réduit les animations.
    this.wantsPlay = !this.reducedMotion;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.carouselBox = this.querySelector('.sm-vs__carousel-box');
    this.carouselBox?.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
      },
      { signal }
    );

    if (this.slides().length === 0) return;

    this.track.addEventListener(
      'scroll',
      () => {
        if (!this._scrollTicking) {
          window.requestAnimationFrame(() => {
            this.renderCarousel();
            this._scrollTicking = false;
          });
          this._scrollTicking = true;
        }
      },
      { passive: true, signal }
    );

    this.slides().forEach((slide) => {
      slide.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.scrollSlideToCenter(slide);
        },
        { signal }
      );

      const video = slide.querySelector('video');
      const toggle = slide.querySelector('.sm-vs__toggle');
      if (!video || !toggle) return;

      toggle.hidden = false;
      toggle.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.wantsPlay = video.paused;
          this.syncPlayback();
        },
        { signal }
      );
      video.addEventListener('play', () => this.updateToggle(toggle, true), { signal });
      video.addEventListener('pause', () => this.updateToggle(toggle, false), { signal });
    });

    this.btnPrev?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.stepSlide(-1);
      },
      { signal }
    );
    this.btnNext?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.stepSlide(1);
      },
      { signal }
    );

    this.btnPrev?.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.stopPropagation();
        this.stepSlide(-1);
      },
      { signal }
    );
    this.btnNext?.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.stopPropagation();
        this.stepSlide(1);
      },
      { signal }
    );

    window.addEventListener(
      'resize',
      () => {
        window.requestAnimationFrame(() => this.renderCarousel());
      },
      { signal }
    );

    document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });

    // Posters : un peu avant que le carrousel n'arrive à l'écran, pour qu'il n'apparaisse pas vide.
    this.posterObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.loadPosters();
        this.posterObserver?.disconnect();
      },
      { rootMargin: '300px 0px' }
    );
    this.posterObserver.observe(this);

    // Vidéo : seulement quand le carrousel est réellement visible.
    this.viewObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        this.inView = entry.isIntersecting;
        this.syncPlayback();
      },
      { threshold: 0.25 }
    );
    this.viewObserver.observe(this.track);

    this.renderCarousel();
    setTimeout(() => this.renderCarousel(), 150);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.posterObserver?.disconnect();
    this.viewObserver?.disconnect();
  }

  scrollSlideToCenter(slide) {
    const track = this.track;
    const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
    const scrollTarget = slideCenter - track.clientWidth / 2;
    track.scrollTo({
      left: scrollTarget,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });
  }

  stepSlide(delta) {
    const slides = this.slides();
    const s = this.currentActiveSlide;
    if (!s || slides.length === 0) return;
    const idx = slides.indexOf(s);
    if (idx < 0) return;
    const target = delta < 0 ? slides[idx - 1] : slides[idx + 1];
    if (target) this.scrollSlideToCenter(target);
  }

  loadPosters() {
    this.querySelectorAll('video[data-poster]').forEach((video) => {
      video.poster = video.dataset.poster;
      video.removeAttribute('data-poster');
    });
  }

  /**
   * Pose les sources d'une vidéo, une seule fois. Avant cet appel, la vidéo n'a aucune source.
   * @param {HTMLVideoElement} video
   */
  loadVideo(video) {
    const sources = video.querySelectorAll('source[data-src]');
    if (sources.length === 0) return;
    sources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.load();
  }

  /**
   * Seule la vidéo centrale joue, et seulement si le carrousel est visible, l'onglet affiché et la
   * lecture voulue. Toutes les autres sont en pause.
   */
  syncPlayback() {
    const active = this.currentActiveSlide;
    const canPlay = this.inView && !document.hidden && this.wantsPlay;

    this.slides().forEach((slide) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (slide === active && canPlay) {
        this.loadVideo(video);
        if (video.paused) video.play().catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }
    });
  }

  /**
   * @param {HTMLButtonElement} toggle
   * @param {boolean} playing
   */
  updateToggle(toggle, playing) {
    toggle.classList.toggle('is-playing', playing);
    const label = playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay;
    if (label) toggle.setAttribute('aria-label', label);
  }

  renderCarousel() {
    const slides = this.slides();
    if (slides.length === 0) return;

    const track = this.track;
    const trackCenter = track.scrollLeft + track.clientWidth / 2;

    let closestSlide = null;
    let minDistance = Infinity;
    const gap = parseFloat(getComputedStyle(track).gap) || 24;

    slides.forEach((slide) => {
      const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(trackCenter - slideCenter);
      const maxDistance = slide.clientWidth + gap;
      let ratio = distance / maxDistance;
      ratio = Math.max(0, Math.min(1, ratio));

      const scale = 1 - ratio * 0.12;
      const blur = ratio * 6;
      const opacity = 1 - ratio * 0.35;

      slide.style.transform = `scale(${scale})`;
      slide.style.filter = `blur(${blur}px)`;
      slide.style.opacity = String(opacity);

      if (distance < minDistance) {
        minDistance = distance;
        closestSlide = slide;
      }
    });

    if (!closestSlide) return;
    const activeChanged = closestSlide !== this.currentActiveSlide;
    this.currentActiveSlide = closestSlide;

    const single = slides.length <= 1;
    const idx = slides.indexOf(closestSlide);
    if (this.btnPrev) {
      this.btnPrev.classList.toggle('is-hidden', single || idx <= 0);
    }
    if (this.btnNext) {
      this.btnNext.classList.toggle('is-hidden', single || idx >= slides.length - 1);
    }

    const link = closestSlide.dataset.link || '#';
    const title = closestSlide.dataset.title || '';

    if (this.btn) {
      this.btn.setAttribute('href', link);
      if (this.openNewTab) {
        this.btn.setAttribute('target', '_blank');
        this.btn.setAttribute('rel', 'noopener noreferrer');
      } else {
        this.btn.removeAttribute('target');
        this.btn.removeAttribute('rel');
      }
      if (link === '#' || !link) {
        this.btn.setAttribute('aria-disabled', 'true');
        this.btn.classList.add('sm-vs__cta--muted');
      } else {
        this.btn.removeAttribute('aria-disabled');
        this.btn.classList.remove('sm-vs__cta--muted');
      }
    }
    if (this.btnText) {
      this.btnText.textContent = title;
    }

    if (activeChanged) {
      slides.forEach((s) => s.classList.toggle('is-active', s === closestSlide));
      this.syncPlayback();
    }
  }
}

if (!customElements.get('sm-video-showcase')) {
  customElements.define('sm-video-showcase', SmVideoShowcase);
}
